// #43 RootsHeat (DC-15): one tile per ROOTS value — the value, the rounds that assess it, a coverage bar and the
// owners' initials; a value no round covers reads *Uncovered* on a red bar. A tile that jumps to its round is a link.
// #48 RootsRadar: the five values on a 1–5 pentagon (kept in the library; no V2 family draws it, Q-E27). One image
// named with every value; the polygon is his blue at .22 with a --blue-text edge. It scales to its container.

import type { ReactNode } from "react";
import { Avatars } from "../core";
import { ROOTS_LABELS, ROOTS_ORDER, type RootsValue } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { vars } from "../lib/vars";
import { METRIC_LABELS } from "./labels";

export interface RootsHeatItem {
  value: RootsValue;
  /** The rounds that assess it (one line each); empty → *Uncovered*. */
  rounds: readonly string[];
  /** 0–100 coverage. */
  pct: number;
  owners?: readonly { name: string; hueKey?: string | undefined }[] | undefined;
  /** The round it jumps to. */
  href?: string | undefined;
  hs?: Hs | undefined;
}

function HeatTile({ item }: { item: RootsHeatItem }) {
  const hot = useHs(item.hs);
  const uncovered = item.rounds.length === 0;
  const pct = Math.max(0, Math.min(100, item.pct));
  const body: ReactNode = (
    <>
      <span className="mc-htl">
        <b>{ROOTS_LABELS[item.value]}</b>
        <span className="mc-hrl">
          {uncovered ? <span className="mc-ht-unc">{METRIC_LABELS.uncovered}</span> : item.rounds.map((round, index) => <span key={index}>{round}</span>)}
        </span>
      </span>
      <span className="mc-hbt" aria-hidden="true">
        <i style={vars({ "--mc-pct": `${uncovered ? 100 : pct}%`, "--mc-bc": uncovered ? "var(--red-text)" : "var(--green-text)" })} />
      </span>
      {item.owners?.length ? <Avatars items={item.owners} size={24} /> : null}
    </>
  );
  if (item.href !== undefined) return <a className={cx("mc-ht", "mc-ht-link", uncovered && "mc-ht-u")} href={item.href} {...hot}>{body}</a>;
  return <div className={cx("mc-ht", uncovered && "mc-ht-u")}>{body}</div>;
}

export interface RootsHeatProps {
  items: readonly RootsHeatItem[];
  className?: string | undefined;
}

export function RootsHeat({ items, className }: RootsHeatProps) {
  return <div className={cx("mc-heat", className)}>{items.map((item) => <HeatTile key={item.value} item={item} />)}</div>;
}

export interface RootsRadarProps {
  /** 1–5 per value. */
  values: Readonly<Record<RootsValue, number>>;
  /** The polygon's diameter in px (default 140). */
  size?: number | undefined;
  className?: string | undefined;
}

export function RootsRadar({ values, size = 140, className }: RootsRadarProps) {
  const r0 = size / 2;
  const padX = Math.round(0.12 * r0 + 68);
  const padT = Math.round(0.18 * r0 + 20);
  const padB = 30;
  const width = 2 * r0 + 2 * padX;
  const height = 2 * r0 + padT + padB;
  const cx0 = width / 2;
  const cy0 = padT + r0;
  const point = (i: number, v: number): [number, number] => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    return [cx0 + (Math.cos(a) * r0 * v) / 5, cy0 + (Math.sin(a) * r0 * v) / 5];
  };
  const pts = (v: (i: number) => number) => ROOTS_ORDER.map((_, i) => point(i, v(i)).map((x) => x.toFixed(1)).join(",")).join(" ");
  const value = (key: RootsValue) => Math.max(0, Math.min(5, values[key]));
  const name = `${ROOTS_LABELS.roots}: ${ROOTS_ORDER.map((key) => `${ROOTS_LABELS[key]} ${value(key).toFixed(1)}`).join(", ")}`;
  return (
    <svg
      className={cx("mc-radar", className)} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={name}
      style={vars({ "--mc-w": width })} focusable="false"
    >
      {[1, 2, 3, 4, 5].map((level) => <polygon key={level} className="mc-radar-grid" points={pts(() => level)} />)}
      {ROOTS_ORDER.map((key, i) => {
        const [x, y] = point(i, 5);
        return <line key={key} className="mc-radar-grid" x1={cx0} y1={cy0} x2={x.toFixed(1)} y2={y.toFixed(1)} />;
      })}
      <polygon className="mc-radar-area" points={pts((i) => value(ROOTS_ORDER[i] ?? "respect"))} />
      {ROOTS_ORDER.map((key, i) => {
        const [x, y] = point(i, value(key));
        return <circle key={key} className="mc-radar-dot" cx={x.toFixed(1)} cy={y.toFixed(1)} r="3.5" />;
      })}
      {ROOTS_ORDER.map((key, i) => {
        const [x, y] = point(i, 5.9);
        const v = value(key).toFixed(1);
        if (i === 0) {
          return <text key={key} x={x.toFixed(1)} y={(y - 2).toFixed(1)} textAnchor="middle" aria-hidden="true">{ROOTS_LABELS[key]}{" "}<tspan className="mc-radar-v">{v}</tspan></text>;
        }
        const right = x > cx0;
        const tx = (x + (right ? 2 : -2)).toFixed(1);
        const ty = (i === 1 || i === 4 ? y - 4 : y + 8).toFixed(1);
        return (
          <text key={key} x={tx} y={ty} textAnchor={right ? "start" : "end"} aria-hidden="true">
            {ROOTS_LABELS[key]}<tspan className="mc-radar-v" x={tx} dy="14">{v}</tspan>
          </text>
        );
      })}
    </svg>
  );
}
